import { useState } from "react";
import { Controller } from "react-hook-form";
import { Alert } from "@/components/alert";
import { Button } from "@/components/button";
import { Toggle } from "@/components/toggle";
import { Modal } from "@/components/modal";
import { Form, FormField, FormSubmit } from "@/src/forms";
import { createWebhook } from "@/src/services/webhook_service";
import { addWebhookSchema, type AddWebhookValues } from "../_schemas/add-webhook";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Fired after the backend creates the webhook; the parent reloads the list + shows a success toast. */
  onCreated: () => void;
  /** Forwarded to Modal — the outlet context's `mainInsetLeft`, so the dialog
   *  centers relative to the sidebar-aware content area. */
  insetLeft?: number;
};

export function AddWebhookModal({ open, onClose, onCreated, insetLeft }: Props) {
  const [createError, setCreateError] = useState<string | null>(null);

  function handleClose() {
    setCreateError(null);
    onClose();
  }

  async function handleValid(values: AddWebhookValues) {
    setCreateError(null);
    const res = await createWebhook(values.url, values.sandbox);
    if (!res.ok) {
      setCreateError(res.error.message);
      return;
    }
    onCreated();
    handleClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add webhook"
      description="We send webhook event data to your endpoint when an event happens."
      insetLeft={insetLeft}
    >
      <Form
        schema={addWebhookSchema}
        defaultValues={{ url: "", sandbox: false }}
        onSubmit={handleValid}
        className="flex flex-col gap-4"
      >
        {createError && (
          <Alert tone="danger" role="alert">
            {createError}
          </Alert>
        )}
        <FormField
          name="url"
          label="Endpoint URL"
          type="url"
          placeholder="https://api.example.com/sanctions/webhook"
          hint="Must use HTTPS."
        />
        <div className="flex items-center justify-between gap-4 rounded-[4px] bg-grey-50 px-3 py-2">
          <div className="flex flex-col">
            <span className="text-small font-medium text-black">Sandbox</span>
            <span className="text-tiny text-grey-900">
              Receive events from the sandbox environment instead of live.
            </span>
          </div>
          <Controller
            name="sandbox"
            render={({ field }) => (
              <Toggle checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
            )}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button hierarchy="secondary" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <FormSubmit hierarchy="primary" pendingLabel="Adding…">
            Add webhook
          </FormSubmit>
        </div>
      </Form>
    </Modal>
  );
}
